import React, { FunctionComponent, ReactNode } from 'react'
import { BaseIcon, DarkMode, DesignLanguage, Heading, Text, Grid, Link, Message, Theme, ThemeContext, ThemeProps, getCountryEmoji } from '@afterpaytouch/core'
import { Trans, useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useConsumerSizes } from '../../../utils/responsive'
import {
  useConsumerCashIdentity,
  useConsumerContactAddress,
  useConsumerEmail,
  useConsumerIsCashCohort,
  useConsumerMobile,
  useConsumerPreferredLocale,
} from '../../../state'
import { Head } from '../../Head'
import { CloseOrReactivateAccountButton } from '../../CloseOrReactivateAccountButton'
import { ConsumerAddress } from '@afterpaytouch/portal-api'
import { mapLocales } from '../../../utils/mapLocales'
import { useEditEmailAddressModal, EditEmailAddressModal } from '../../EditEmailAddressModal'
import { useEditMobileNumberModal, EditMobileNumberModal } from '../../EditMobileNumberModal'
import { useEditAddressModal, EditAddressModal } from '../../EditAddressModal'
import { useEditLanguageModal, EditLanguageModal } from '../../EditLanguageModal'
import { useFormatMobile } from '../../../hooks'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { getCashAppLink } from '../../../utils/redirects'
import { DetailRow } from '../DetailRow/DetailRow'
import { SupportedLocale } from '@afterpay/types'

const I18N_NAMESPACE = ['common', 'account']

const cashAppIconTheme: ThemeProps = {
  theme: {
    selected: Theme.Pegasus,
    darkMode: DarkMode.System,
    isDarkModeActive: false,
    setTheme: () => {},
    setDarkMode: () => {},
    designLanguage: DesignLanguage.Arcade,
  },
}

export const formatResidentialAddress = (addr: ConsumerAddress | null | undefined): string => {
  if (addr == null) {
    return ''
  }
  const substring = `, ${addr.suburb} ${addr.state} ${addr.postcode}`
  return `${addr.address1.includes(substring) ? addr.address1.split(substring)[0] : addr.address1}${
    addr.address2 != null && addr.address2.length > 0 ? `, ${addr.address2}` : ''
  }, ${addr.suburb != null && addr.suburb.length > 0 ? `${addr.suburb}, ` : ''}${addr.state != null && addr.state.length > 0 ? `${addr.state}, ` : ''}${
    addr.postcode
  }`
}

const FormattedMobileWithEmoji = ({ mobile, country }): JSX.Element => {
  const formattedMobile = useFormatMobile(mobile)
  const countryEmoji = getCountryEmoji(country) ?? ''
  if (formattedMobile !== '' && countryEmoji !== '') {
    return (
      <>
        <span className='mr-2'>{countryEmoji}</span>
        <span>{formattedMobile}</span>
      </>
    )
  } else {
    // @ts-ignore: OPERATION BLEED STOPPER
    return null
  }
}

const AliasList: FunctionComponent<{
  primary: ReactNode
  primaryValue: string
  aliases: string[]
  renderAlias: (alias: string) => ReactNode
}> = ({ primary, primaryValue, aliases, renderAlias }) => {
  const extras = aliases.filter((a) => a !== primaryValue)
  if (extras.length === 0) {
    return <>{primary}</>
  }
  return (
    <>
      <span className='block leading-8'>{primary}</span>
      {extras.map((alias) => (
        <span key={alias} className='block leading-8'>
          {renderAlias(alias)}
        </span>
      ))}
    </>
  )
}

const CashAppAddressDisclaimer: FunctionComponent<{ address: string; disclaimer: string }> = ({ address, disclaimer }) => (
  <>
    {address}
    <span className='mt-2 block text-xs italic text-gray-500'>{disclaimer}</span>
  </>
)

const renderResidentialAddress = (address: string, isCashCohort: boolean, disclaimer: string): ReactNode => {
  if (!isCashCohort) {
    return address
  }
  return <CashAppAddressDisclaimer address={address} disclaimer={disclaimer} />
}

const CashAppIcon: FunctionComponent = () => (
  <span className='mr-1 inline-flex h-6 w-6 items-center justify-center' aria-hidden>
    <ThemeContext.Provider value={cashAppIconTheme}>
      <BaseIcon name='Cashapp' size='Small' />
    </ThemeContext.Provider>
  </span>
)

const CashAppDetailsLabel: FunctionComponent<{ label: string }> = ({ label }) => (
  <span className='inline-flex items-center text-sm font-normal text-gray-600'>
    <CashAppIcon />
    <span>{label}</span>
  </span>
)

export const ProfileTab: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const router = useRouter()

  const locale = router?.locale as SupportedLocale
  const country = mapLocales('locale', locale)
  const { subheadingSize } = useConsumerSizes()
  const email = useConsumerEmail()
  const mobile = useConsumerMobile()
  const contactAddress = useConsumerContactAddress()
  const preferredLocale = useConsumerPreferredLocale()
  const isCashCohort = useConsumerIsCashCohort()
  const cashIdentity = useConsumerCashIdentity()

  const formattedMobile = <FormattedMobileWithEmoji mobile={mobile} country={country} />
  const residentialAddress = formatResidentialAddress(contactAddress)

  const emailValue: ReactNode =
    cashIdentity.emails.length > 0 ? (
      <AliasList primary={email} primaryValue={email ?? ''} aliases={cashIdentity.emails} renderAlias={(alias) => alias} />
    ) : (
      email
    )

  const mobileValue: ReactNode =
    cashIdentity.phoneNumbers.length > 0 ? (
      <AliasList
        primary={formattedMobile}
        primaryValue={mobile ?? ''}
        aliases={cashIdentity.phoneNumbers}
        renderAlias={(alias) => <FormattedMobileWithEmoji mobile={alias} country={country} />}
      />
    ) : (
      formattedMobile
    )

  const isEditLanguageEnabled = (locale === 'en-CA' || locale === 'fr-CA') && (preferredLocale === 'en-CA' || preferredLocale === 'fr-CA')

  const pageTitle = `${t('account:tab:profile:title')} - ${t('account:title')} | ${t('common:brandName')}`

  const [isEditEmailAddressModalOpen, { openModal: openEditEmailAddressModal, closeModal: closeEditEmailAddressModal }] = useEditEmailAddressModal()
  const [isEditMobileNumberModalOpen, { openModal: openEditMobileNumberModal, closeModal: closeEditMobileNumberModal }] = useEditMobileNumberModal()
  const [isEditAddressModalOpen, { openModal: openEditAddressModal, closeModal: closeEditAddressModal }] = useEditAddressModal()
  const [isEditLanguageModalOpen, { openModal: openEditLanguageModal, closeModal: closeEditLanguageModal }] = useEditLanguageModal()

  return (
    <MaskedElement>
      <Head title={pageTitle} />
      {isCashCohort ? (
        <div className='flex flex-wrap items-center gap-4'>
          <Heading bold size={subheadingSize} testNameSpace='body-title'>
            {t('account:tab:profile:body:title')}
          </Heading>
          <CashAppDetailsLabel label={t('account:tab:profile:body:cashCohort:syncedWithCashApp')} />
        </div>
      ) : (
        <Heading bold size={subheadingSize} testNameSpace='body-title'>
          {t('account:tab:profile:body:title')}
        </Heading>
      )}
      <div className='mt-8'>
        <Grid>
          <Grid.Item sm={12} lg={6}>
            <DetailRow
              label={t('account:tab:profile:body:labelEmail')}
              value={emailValue}
              isEditable={!isCashCohort}
              onClick={openEditEmailAddressModal}
              testNameSpace='edit-email'
            />
            <DetailRow
              label={t('account:tab:profile:body:labelMobile')}
              value={mobileValue}
              isEditable={!isCashCohort}
              onClick={openEditMobileNumberModal}
              testNameSpace='edit-mobile'
            />
            <DetailRow
              label={t(isCashCohort ? 'account:tab:profile:body:labelResidentialAddressCashCohort' : 'account:tab:profile:body:labelResidentialAddress')}
              value={renderResidentialAddress(residentialAddress, isCashCohort, t('account:tab:profile:body:cashCohort:addressDisclaimer'))}
              isEditable
              onClick={openEditAddressModal}
              testNameSpace='edit-address'
              hideDivider={isCashCohort && !isEditLanguageEnabled}
            />
            {isEditLanguageEnabled && (
              <DetailRow
                label={t('account:tab:profile:body:labelLanguage')}
                value={
                  {
                    'en-CA': t('account:edit:language:content:en-CA'),
                    'fr-CA': t('account:edit:language:content:fr-CA'),
                  }[preferredLocale]
                }
                isEditable
                onClick={openEditLanguageModal}
                testNameSpace='edit-language'
                hideDivider={isCashCohort}
              />
            )}
            {!isCashCohort && (
              <div className='mb-8'>
                <CloseOrReactivateAccountButton />
              </div>
            )}
          </Grid.Item>
          <Grid.Item sm={12} lg={6}>
            <div className='lg:flex lg:flex-row lg:justify-center lg:px-8'>
              <div className='max-w-[368px]'>
                {isCashCohort ? (
                  <Message
                    kind='Success'
                    iconName='Lock'
                    size='Regular'
                    heading={t('account:tab:profile:body:message:cashCohort:title')}
                    description={
                      <Text size='XS'>
                        <Trans
                          i18nKey='account:tab:profile:body:message:cashCohort:description'
                          components={{
                            cashAppSettingsLink: <Link href={getCashAppLink('/account/settings')} target='_blank' kind='Underline' />,
                          }}
                        />
                      </Text>
                    }
                  />
                ) : (
                  <Message
                    kind='Success'
                    iconName='Lock'
                    size='Regular'
                    heading={t('account:tab:profile:body:message:title')}
                    description={
                      <Text size='XS'>
                        <Trans i18nKey={'account:tab:profile:body:message:description'} />
                      </Text>
                    }
                  />
                )}
              </div>
            </div>
          </Grid.Item>
        </Grid>
      </div>
      <EditEmailAddressModal isModalOpen={isEditEmailAddressModalOpen} closeModal={closeEditEmailAddressModal} />
      <EditMobileNumberModal isModalOpen={isEditMobileNumberModalOpen} closeModal={closeEditMobileNumberModal} />
      <EditAddressModal isModalOpen={isEditAddressModalOpen} closeModal={closeEditAddressModal} country={country} />
      {isEditLanguageEnabled && <EditLanguageModal isModalOpen={isEditLanguageModalOpen} closeModal={closeEditLanguageModal} />}
    </MaskedElement>
  )
}
